import { useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'

// NOTE: 'localhost-only' lets CloudSponge access your local contacts for testing only
// we need to use the key when deployed
const cloudspongeKey = import.meta.env.VITE_CLOUDSPONGE_KEY
export const MAX_BULK_IMPORT_CONTACTS = 40

export interface CloudSpongeContact {
  fullName: () => string
  primaryEmail: () => string | undefined
  primaryPhone: () => string | undefined
}

interface CloudSpongeOptions {
  afterSubmitContacts?: (contacts: CloudSpongeContact[], source: string) => void
  [key: string]: unknown
}

interface CloudSpongeWidgetProps {
  onImportContacts: (contacts: CloudSpongeContact[], source: string) => void
}

declare global {
  interface Window {
    cloudsponge?: {
      init: (options: CloudSpongeOptions) => void
    }
  }
}

const CloudSpongeWidget = ({ onImportContacts }: CloudSpongeWidgetProps) => {
  const { t } = useTranslation()
  const scriptLoaded = useRef(false)
  const initialized = useRef(false)

  const loadScript = useCallback((src: string, id: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (id && document.getElementById(id)) {
        resolve()
        return
      }

      const scriptTag = document.createElement('script')
      scriptTag.type = 'text/javascript'
      scriptTag.async = true
      if (id) {
        scriptTag.id = id
      }

      const handleLoad = () => {
        scriptTag.removeEventListener('load', handleLoad)
        scriptTag.removeEventListener('error', handleError)
        resolve()
      }

      const handleError = () => {
        scriptTag.removeEventListener('load', handleLoad)
        scriptTag.removeEventListener('error', handleError)
        reject(new Error(`Failed to load CloudSponge script: ${src}`))
      }

      scriptTag.addEventListener('load', handleLoad)
      scriptTag.addEventListener('error', handleError)

      scriptTag.src = src
      document.body.appendChild(scriptTag)
    })
  }, [])

  useEffect(() => {
    const initCloudSponge = async () => {
      if (scriptLoaded.current || initialized.current) return

      try {
        await loadScript(`https://api.cloudsponge.com/widget/${cloudspongeKey}.js`, '__cloudsponge_widget_script')

        if (window.cloudsponge && !initialized.current) {
          window.cloudsponge.init({
            afterSubmitContacts: (contacts: CloudSpongeContact[], source: string) => {
              onImportContacts(contacts, source)
            },
            selectionLimit: MAX_BULK_IMPORT_CONTACTS,
            selectionLimitMessage: t('content.components.bulk_import.selection_limit_message', {
              max: MAX_BULK_IMPORT_CONTACTS,
            }),
          })
          initialized.current = true
          scriptLoaded.current = true
        }
      } catch (error) {
        Sentry.logger.error('Failed to initialize CloudSponge widget', { error: String(error) })
      }
    }

    initCloudSponge()

    return () => {
      initialized.current = false
    }
  }, [loadScript, onImportContacts, t])

  return (
    <div className='flex flex-col gap-4 px-4 sm:px-6 md:px-8'>
      <div className='text-white text-xl font-serif text-center text-balance'>
        {t('content.components.bulk_import.subtitle')}
      </div>
      <div className='flex flex-wrap justify-center gap-4'>
        <button
          type='button'
          className='cloudsponge-launch px-6 py-3 bg-white text-cta rounded-lg hover:bg-gray-100 transition-colors w-54 shadow-sm shadow-neutral-700  hover:bg-gray-200 active:bg-gray-200 focus:bg-gray-200'
          data-cloudsponge-source='gmail'
        >
          {t('content.components.bulk_import.gmail_button')}
        </button>
        <button
          type='button'
          className='cloudsponge-launch px-6 py-3 bg-white text-cta rounded-lg hover:bg-gray-100 transition-colors w-54 shadow-sm shadow-neutral-700 hover:bg-gray-200 active:bg-gray-200 focus:bg-gray-200'
          data-cloudsponge-source='outlookcom'
        >
          {t('content.components.bulk_import.outlook_button')}
        </button>
        <button
          type='button'
          className='cloudsponge-launch px-6 py-3 bg-white text-cta rounded-lg hover:bg-gray-100 transition-colors w-54 shadow-sm shadow-neutral-700 hover:bg-gray-200 active:bg-gray-200 focus:bg-gray-200'
          data-cloudsponge-source='yahoo'
        >
          {t('content.components.bulk_import.yahoo_button')}
        </button>
      </div>
    </div>
  )
}

export default CloudSpongeWidget
